import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface AuteurJournal { uid?: string; nom?: string; role?: string; roleCode?: string; }

/** Une action du journal d'activité (date UTC sans zone) */
export interface EntreeJournal {
  id: string | number;
  date: string;
  auteur: AuteurJournal;
  action: string;
  elementType?: string;
  elementId?: number | string;
  elementLibelle?: string;
  detailType?: string;
  detailId?: number | string;
  module?: string | null;
  resume: string;
  financier?: boolean;
}

export interface PageJournal { entrees: EntreeJournal[]; curseur: string | null; }

export interface MembreJournal { uid: string; nom: string; role?: string; roleCode?: string; }

export interface FiltresJournal {
  membre?: string; module?: string; type?: string; du?: string; au?: string; elementType?: string; elementId?: string | number;
}

/**
 * Journal d'activité (/api/audit/journal) : droits filtrés par le serveur. Une page peut revenir incomplète,
 * voire vide, avec un curseur : on redemande tant qu'il en reste et que le nombre voulu n'est pas atteint.
 */
@Injectable({ providedIn: 'root' })
export class JournalService {
  private readonly base = `${environment.BASE_URL}/api/audit/journal`;

  constructor(private http: HttpClient) {}

  page(filtres: FiltresJournal, curseur?: string | null, taille = 30): Observable<PageJournal> {
    return this.http.get<PageJournal>(this.base, { params: this.params({ ...filtres, curseur, taille }) });
  }

  pageElement(type: string, id: number | string, curseur?: string | null, taille = 5): Observable<PageJournal> {
    return this.http.get<PageJournal>(`${this.base}/element/${encodeURIComponent(type)}/${id}`,
      { params: this.params({ curseur, taille }) });
  }

  membres(): Observable<MembreJournal[]> {
    return this.http.get<MembreJournal[]>(`${this.base}/membres`);
  }

  /** Au moins `voulu` entrées (ou la fin), en 6 requêtes au plus ; renvoie le curseur suivant */
  auMoins(lire: (curseur: string | null) => Observable<PageJournal>, voulu: number, depuis: string | null = null): Observable<PageJournal> {
    let n = 0;
    let requetes = 0;
    return lire(depuis).pipe(
      expand(p => {
        n += (p.entrees || []).length;
        requetes++;
        return p.curseur && n < voulu && requetes < 6 ? lire(p.curseur) : EMPTY;
      }),
      reduce((acc: PageJournal, p: PageJournal) => ({ entrees: [...acc.entrees, ...(p.entrees || [])], curseur: p.curseur ?? null }),
        { entrees: [], curseur: null } as PageJournal)
    );
  }

  private params(o: any): any {
    const p: any = {};
    for (const [k, v] of Object.entries(o)) { if (v !== undefined && v !== null && v !== '') { p[k] = String(v); } }
    return p;
  }
}
